import { ServiceRequest } from '../app/ServiceRequest.models';
import { ErrorCode, Scenario, defaultLocales } from "../app/app.models";
import { OcCustomContextExtensionAttributeKeys, QueryParamKeys } from '../app/constants';

export const getUrlSearchParams = (lowerCaseKeys: boolean = true, removeKeys: string[] = []) => {
  const originalParams = new URLSearchParams(window.location.search);
  let resultParams = new URLSearchParams();
  let resultRemoveKeys = removeKeys;
  
  if (!lowerCaseKeys) {
    resultParams = originalParams;
  } else {
    originalParams.forEach((value, key) => {
      resultParams.append(key?.toLowerCase(), value);
    });
    if (!!removeKeys) {
      resultRemoveKeys = removeKeys.map(key => key?.toLowerCase());
    }
  }
  
  if (!!resultRemoveKeys) {
    resultRemoveKeys.forEach(key => {
      resultParams.delete(key);
    });
  }

  return resultParams;
};

export const getUrlSearchParam = (name: string, lowerCaseKeys: boolean = true) => {
  var urlSearchParams = getUrlSearchParams(lowerCaseKeys);
  return urlSearchParams.get(name);
};

const validateLocale = (locale: string): boolean => {
  if (!locale) {
    return false;
  }

  const localeInfo: string[] = locale.split('-');
  return !!localeInfo && localeInfo.length >= 2;
};

const reformatLocale = (locale: string): string => {
  if (!locale) {
    return 'en-US';
  }

  const localeArray: string[] = locale.split('-');
  if (!localeArray) {
    return 'en-US';
  }

  if (!!localeArray && localeArray.length < 2) {
    // navigator.language only contains language, but not country/region info
    Object.keys(defaultLocales).forEach((language: string) => {
      if (!!language
        && !!defaultLocales[language]
        && !!localeArray[0]
        && language.toLowerCase() == localeArray[0].toLowerCase()) {
        return `${localeArray[0]}-${defaultLocales[language]}`;
      }
    });
  }

  return locale;
};

export const getLocale = (): string => {
  // Check if locale is in url path
  let urlPath = window.location.pathname;
  if (!!urlPath && urlPath.length > 1) {
    urlPath = urlPath.substring(1);
    let pathArray = urlPath.split('/');
    if (
      pathArray.length > 0
      && pathArray[0].search('[a-zA-Z][a-zA-Z]-[a-zA-Z][a-zA-Z]') !== -1
      && validateLocale(pathArray[0])
    ) {
      return pathArray[0];
    }
  }

  // Check if browser language is detected, it may be null
  if (!!navigator && !!navigator.language) {
    if (validateLocale(navigator.language)) {
      return navigator.language;
    } else {
      return reformatLocale(navigator.language);
    }
  }

  return 'en-US';
};

export const addLocaleToUrl = (originalUrl: string): string => {
  const locale = getLocale();
  let destinationUrl = originalUrl;
  if (!!locale) {
    if (!destinationUrl || destinationUrl[0] !== '/') {
      destinationUrl = `/${destinationUrl}`;
    }
    destinationUrl = `/${locale}${destinationUrl}`;
  }
  return destinationUrl;
};

export const addSourceAppToSearchParams = (searchParams: {[key: string]: string}): void => {
  var sourceApp = getUrlSearchParam(QueryParamKeys.SourceApp);
  console.log('sourceapp %j: ', sourceApp);
  if (!!sourceApp) {
    searchParams['sourceapp'] = sourceApp;
  }
  console.log('queryParams %j: ', searchParams);
};

export const cleanSearchParams = (searchParams: { [key: string]: string }): void => {
  const cleanParams: { [key: string]: string } = {};
  Object.keys(searchParams)
    .forEach((k) => {
      if (!!searchParams[k]) {
          cleanParams[k] = searchParams[k];
      }
    }
  );
  searchParams = cleanParams;
  console.log('cleanParams %j: ', cleanParams);
};

// Get CustomContext with properties only from URL
export const getCustomContextFromUrl = (lowerCaseValues = false) => {
  let customContextStr = decodeURIComponent(getUrlSearchParam(QueryParamKeys.CustomContext));
  if (!!customContextStr && !!lowerCaseValues) {
    customContextStr = customContextStr.toLowerCase();
  }
  return JSON.parse(customContextStr);
};

// Combine the properties from both the URL and the service request,
// and return as combined CustomContext object
export const getCustomContext = () => {
  var customContext = getCustomContextFromUrl();
  var serviceRequest = getServiceRequest();

  if (!!serviceRequest) {
    customContext.Source = serviceRequest.Source;
    customContext.LCID = serviceRequest.UserLcid;
    customContext.PUID = serviceRequest.UserPuid;
    OcCustomContextExtensionAttributeKeys.forEach(key => {
      const value = serviceRequest?.ExtensionAttributes[key];
      if (!!value) {
        customContext[key] = value;
      }
    });
  }

  // Context variable 'QueryParams' will be used by OC to generate the agent provided rejoin link.
  // OC will append the value as query params to the 'Portal URL' value set in the OC 'Reconnect to previous chat' setting.
  // Query param 'oc.reconnectid' is handled by OC, and we want to skip it here.
  const queryParams = getUrlSearchParams(true, [
    QueryParamKeys.ReconnectId,
    QueryParamKeys.Theme
  ]);
  customContext.QueryParams = queryParams.toString();

  return customContext;
};

export const getRequestIdFromUrl = () => {
  return getCustomContextFromUrl(true)?.casenumber;
};

// Convert the custom context JSON object from Url to the format Omnichannel expects
// Example returned customContext
// var customContext = {
//   CaseNumber: {
//     value: '2303150010001150',
//     isDisplayable: true
//   },
//   Source: {
//     value: 'SignUp',
//     isDisplayable: true
//   }
// }
export const getCustomContextForOC = () => {
  var customContextForOC = {};
  var customContext = getCustomContext();
  if (!!customContext) {
    const entries = Object.entries(customContext);
    entries.forEach(([key, value]) => {
      customContextForOC[key] = {
        value: value,
        isDisplayable: true
      };
    });
  }
  return customContextForOC;
};

const getOcContext = () => {
  return (window as any).ocContext;
};

export const getCorrelationId = (): string => {
  return getOcContext()?.correlationId;
};

export const getErrorCode = () => {
  return getOcContext()?.errorCode as ErrorCode;
};

export const getScenario = () => {
  return getOcContext()?.scenario as Scenario;
};

export const getCdnPath = () => {
  return getOcContext()?.cdnPath;
};

export const getWidgetInstanceRequestIdFlag = (): boolean => {
  return getOcContext()?.useRequestIdForWidgetInstance;
};

export const getOmnichannelChatConfig = () => {
  // Default config in DfM PPE environment
  var omnichannelConfig = {
    orgId: 'e99eebac-5dfd-4ddc-94c7-bf5a4ddf945d',
    orgUrl: 'https://org603ce263-crm.omnichannelengagementhub.com',
    widgetId: '095a7c1a-121f-4563-abfa-5c688b00d044'
  };
  
  const ocContext = getOcContext();
  if (!!ocContext
    && !!ocContext.chatConfig
    && !!ocContext.chatConfig.orgId
    && !!ocContext.chatConfig.orgUrl
    && !!ocContext.chatConfig.widgetId) {
      omnichannelConfig = {
        orgId: ocContext.chatConfig.orgId,
        orgUrl: ocContext.chatConfig.orgUrl,
        widgetId: ocContext.chatConfig.widgetId
      };
  }

  return omnichannelConfig;
};

export const getServiceRequest = (): ServiceRequest => {
  return getOcContext()?.serviceRequest;
};

// An easy way to override object properties is using spread operator: var result = {...target, ...source};
// But it only copies the first level properties, so any nested properties in the target may be cleared if the source does not contain them.
// Example code:
// var target = {
//   property1: true,
//   property2: {
//     property21: true
//   }
// };
// var source = {
//   property2: {
//     property22: true
//   },
//   property3: true,
// };
// var result = { ...target, ...source };
// We will get result as below - result.property2.property21 is cleared,
// as source.property2 overrides the whole target.property2
// {
//   property1: true,
//   property2: {
//     property22: true
//   },
//   property3: true,
// };
// So we come up with this function to recursively override target in place, with any nested properties from source.
// If overrideProperties is provided, it contains the property names that we want to override as a whole object,
// and we will skip nested properties within these properties.
// If overrideProperties is not provided, we will recursively override any leaf property from source.
export const mergePropertiesDeeply = (target: Object, source: Object, overrideProperties: string[] = []): void => {
  if (!source) {
    return;
  }

  const sourceKeys = Object.keys(source);
  sourceKeys.forEach((sourceKey: string) => {
    const sourceValue = source[sourceKey];
    // sourceValue will override target as the whole object
    if (overrideProperties.indexOf(sourceKey) >= 0
      || (!!sourceValue
        // sourceValue is the leaf property, override target[sourceKey].
        && (Object.keys(sourceValue).length == 0)
          // target[sourceKey] does not exist, set it to sourceValue directly.
          || !target[sourceKey])) {
      target[sourceKey] = { ...sourceValue };
    } else {
      mergePropertiesDeeply(target[sourceKey], sourceValue, overrideProperties);
    }
  });
};
